import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { useGetUserByEmailLazyQuery } from 'generated/graphql';
import debounce from 'lodash/debounce';
import { useCallback, useMemo } from 'react';
import AuthService from 'services/AuthService';
import { AmplifyAuth, AmplifyHub } from 'v2/core/clients/authAmplify';
import useCreateUserOnEdgeServer from 'v2/core/repository/edge-server/user/useCreateUserOnEdgeServer';
import { useProcessInviteCodeForAuth } from 'v2/core/services/auth/useProcessInviteCodeForAuth';
import { EdgeServerUserInput } from 'v2/views/layouts/authenticationModule/authTypes';

const getToken = (): Promise<CognitoUserSession> =>
  AmplifyAuth.currentSession();

interface ListenAuthInput {
  edgeServerPayload: EdgeServerUserInput;
  onLogin: () => void;
  setAuthState?: () => void;
  signOut?: () => void;
  setErrorMessage?: (errorMessage: string) => void;
}

const useListenAuth = ({
  edgeServerPayload,
  onLogin,
  setAuthState,
  signOut,
  setErrorMessage,
}: ListenAuthInput) => {
  const { createUser } = useCreateUserOnEdgeServer({
    payload: edgeServerPayload,
    onCompleted: () => {},
    onError: () => {},
  });

  const { processInviteCodeIfPresent } = useProcessInviteCodeForAuth();

  const [getUserByEmail] = useGetUserByEmailLazyQuery();

  const continueAfterSignIn = useCallback(
    (returnURL?: string) => {
      getToken()
        .then(async (userToken: CognitoUserSession) => {
          AuthService.setEmail(userToken.getIdToken().payload.email);
          AuthService.setToken(userToken.getIdToken().getJwtToken());
          const isGoogleAuth = AuthService.isGoogleAuth();
          if (edgeServerPayload.email && isGoogleAuth) {
            await createUser();
            await processInviteCodeIfPresent();
            await getUserByEmail({
              variables: {
                email: edgeServerPayload.email,
                slug: AuthService.getTenantSlug(),
              },
            });

            if (returnURL) {
              window.location.href = decodeURIComponent(returnURL);
              return;
            }

            onLogin();
          } else {
            setAuthState?.();

            if (returnURL) {
              window.location.href = decodeURIComponent(returnURL);
              return;
            }

            onLogin();
          }
        })
        .catch((e) => {
          setErrorMessage?.(e.message);
        });
    },
    [
      createUser,
      edgeServerPayload.email,
      getUserByEmail,
      onLogin,
      processInviteCodeIfPresent,
      setAuthState,
      setErrorMessage,
    ],
  );

  const debouncedContinueAfterSignIn = useMemo(
    () => debounce(continueAfterSignIn, 300, { trailing: true }),
    [continueAfterSignIn],
  );

  const listenHub = useCallback(() => {
    AmplifyHub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          debouncedContinueAfterSignIn();
          break;
        case 'customOAuthState':
          debouncedContinueAfterSignIn(data);
          break;
        case 'signOut':
          signOut?.();
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
        default:
          break;
      }
    });
  }, [debouncedContinueAfterSignIn, signOut]);

  return listenHub;
};
export default useListenAuth;
